import React, { useCallback, useEffect } from 'react';
import { Stack, RadioButton, Overlay, LoadingBalls } from '@moda/om';
import { reportError } from '../../../lib/reportError';
import { tc } from '../../../lib/trackingContext';
import { MIN_AFFIRM_PRICE, MAX_AFFIRM_PRICE, PaymentSource } from '../../../constants';
import { useAddressesQuery } from '../../../hooks/useAddress';
import {
  useCart,
  useUpdateCartMutation,
  CartUpdateAction,
  CartUpdatePropertyName
} from '../../../hooks/useCart';
import { useCheckoutStepTracking, CheckoutStep } from '../../../hooks/useCheckout';
import { useCountry } from '../../CountrySelector';
import { useErrorModal } from '../../../hooks/useErrorModal';

import './CheckoutPaymentMethodOptions.scss';

export const CheckoutPaymentMethodOptions: React.FC = () => {
  const { country } = useCountry();
  const { showErrorModal } = useErrorModal();
  const {
    cart: {
      attributes: {
        addressId,
        paymentSummary: { payToday, payLater },
        paymentSource
      }
    }
  } = useCart();
  const [updateCart, { loading }] = useUpdateCartMutation();
  const { addresses } = useAddressesQuery();
  const { trackCheckoutStepComplete } = useCheckoutStepTracking(CheckoutStep.PaymentMethod);

  const handleChange = useCallback(
    async (newPaymentSource: PaymentSource) => {
      if (newPaymentSource === paymentSource) return;

      try {
        await updateCart(CartUpdateAction.PaymentSource, {
          [CartUpdatePropertyName.PaymentSource]: newPaymentSource
        });

        trackCheckoutStepComplete();
      } catch (error) {
        reportError('CHECKOUT_CHANGE_PAYMENT_OPTION', error);
        showErrorModal();
      }
    },
    [paymentSource, updateCart, trackCheckoutStepComplete, showErrorModal]
  );

  const handleChangeToAffirm = useCallback(() => {
    handleChange(PaymentSource.Affirm);
    tc.track('Affirm Payment Selected');
  }, [handleChange]);

  const selectedAddress = addresses.find(address => Number(address.id) === addressId);

  const isAffirmEnabled =
    country.alpha2Code === 'US' &&
    selectedAddress?.attributes.countryCode === 'US' &&
    Number(payLater.price) === 0 &&
    Number(payToday.price) >= MIN_AFFIRM_PRICE &&
    Number(payToday.price) <= MAX_AFFIRM_PRICE;

  // if Affirm is selected and it becomes unavailable (e.g. paying everything with site credit) change to Adyen
  useEffect(() => {
    if (!isAffirmEnabled && paymentSource === PaymentSource.Affirm) {
      updateCart(CartUpdateAction.PaymentSource, {
        [CartUpdatePropertyName.PaymentSource]: PaymentSource.Adyen
      }).then(trackCheckoutStepComplete);
    }
  }, [isAffirmEnabled, paymentSource, trackCheckoutStepComplete, updateCart]);

  if (!isAffirmEnabled) return null;

  return (
    <div className="CheckoutPaymentMethodOptions">
      <Stack direction="horizontal" space={5}>
        <RadioButton
          checked={paymentSource === PaymentSource.Adyen || paymentSource === PaymentSource.Moda}
          onChange={() => handleChange(PaymentSource.Adyen)}
        >
          Debit/Credit Card
        </RadioButton>

        <RadioButton
          checked={paymentSource === PaymentSource.Affirm}
          onChange={handleChangeToAffirm}
        >
          <img
            className="CheckoutPaymentMethodOptions__logo"
            alt="Affirm"
            src="/dist/public/affirm.png"
            width={42}
            height={16}
          />
        </RadioButton>
      </Stack>

      <Overlay show={loading}>
        <LoadingBalls />
      </Overlay>
    </div>
  );
};
